// Paket A12 — Hinweis „Bestandsschutz endet" (Kind `bestandsschutz_end`).
//
// Server-Vertrag (server/bestandsschutz-end-notice.js):
//   GET  /api/membership/bestandsschutz-end
//        → { notice: null | { kind, stage, membership_id, ends_at, end_day, timezone,
//                             can_buy, discount_available } }
//   POST /api/membership/bestandsschutz-end/claim { stage } → { ok, claimed }
//        Vergibt die Stufe atomar; nur bei `claimed: true` darf der Client den
//        Hinweis anzeigen (höchstens einmal je Stufe, auch über mehrere Geräte).
//   POST /api/membership/bestandsschutz-end/ack { stage, action } → { ok, recorded }
//        action: 'dismiss' | 'open' (nur Auswertung)
//
// ⚠️ NICHT Teil des Mitgliedschafts-Promo-Systems (api/promo.ts,
// /api/membership/promo): eigener Endpunkt, eigener Zustand, eigener Host. Ein
// späteres Abschalten der Promos berührt diese Datei nicht.
import { apiRequest } from './client';

export type BestandsschutzEndStage = 'd7' | 'd1' | 'd0';

export interface BestandsschutzEndNotice {
  kind: 'bestandsschutz_end';
  stage: BestandsschutzEndStage;
  membershipId: number;
  /** Ende der Mitgliedschaft, ISO-8601 (UTC). */
  endsAt: string;
  /**
   * Der Ende-Tag, den der Hinweis nennt ('YYYY-MM-DD', Kalendertag in der
   * Kontozeitzone). Vom Server entschieden: ein Ende früh am Morgen (vor 06:00
   * Ortszeit) zählt zum Vortag. Fehlt er (älterer Server), wird das Datum aus
   * `endsAt` und `timezone` abgeleitet.
   */
  endDay?: string;
  /** IANA-Zone des Kontos (users.timezone, Rückfall Europe/Berlin). Leer = Gerätezone. */
  timezone: string;
  /**
   * Nur für volljährige Konten true (Jugendschutz, UWG Anhang Nr. 28). Der
   * Server entscheidet; fehlt oder ist das Feld etwas anderes als `true`, gibt es
   * KEINEN Verweis auf die Mitgliedschaft.
   */
  canBuy: boolean;
  /**
   * Das 25-%-Jahresangebot (bestand25) ist für das Konto noch einlösbar. Nur
   * zusammen mit `canBuy` ausgewertet; der Link auf die Mitgliedschaft hängt dann
   * `discount=bestand25` an, damit die Kaufansicht den zustehenden Preis zeigt.
   */
  discountAvailable?: boolean;
}

export type BestandsschutzEndAckAction = 'dismiss' | 'open';

/** Admin-Vorschau (Einstellungen → Mitgliedschaft): Fenster-Ereignis ohne Server-Zustand. */
export const BESTANDSSCHUTZ_END_PREVIEW_EVENT = 'lorath:bestandsschutz-end-preview';
export interface BestandsschutzEndPreviewDetail {
  canBuy: boolean;
}

const STAGES: readonly BestandsschutzEndStage[] = ['d7', 'd1', 'd0'];
const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/** Defensiv: ein unvollständiger oder fremder Eintrag ergibt „kein Hinweis". */
export function normalizeBestandsschutzEndNotice(value: unknown): BestandsschutzEndNotice | null {
  const raw = record(record(value).notice);
  if (raw.kind !== 'bestandsschutz_end') return null;
  const stage = STAGES.find((candidate) => candidate === raw.stage);
  const membershipId = Number(raw.membership_id);
  const endsAt = typeof raw.ends_at === 'string' ? raw.ends_at : '';
  if (!stage || !Number.isSafeInteger(membershipId) || membershipId <= 0) return null;
  if (!endsAt || Number.isNaN(Date.parse(endsAt))) return null;
  return {
    kind: 'bestandsschutz_end',
    stage,
    membershipId,
    endsAt,
    ...(typeof raw.end_day === 'string' && DAY_KEY.test(raw.end_day) ? { endDay: raw.end_day } : {}),
    timezone: typeof raw.timezone === 'string' ? raw.timezone : '',
    canBuy: raw.can_buy === true,
    discountAvailable: raw.discount_available === true,
  };
}

export async function getBestandsschutzEndNotice(): Promise<BestandsschutzEndNotice | null> {
  return normalizeBestandsschutzEndNotice(
    await apiRequest<unknown>('/api/membership/bestandsschutz-end'),
  );
}

/** true = genau dieser Aufruf darf den Hinweis jetzt anzeigen. */
export async function claimBestandsschutzEndNotice(stage: BestandsschutzEndStage): Promise<boolean> {
  const result = record(await apiRequest<unknown>('/api/membership/bestandsschutz-end/claim', {
    method: 'POST',
    body: { stage },
  }));
  return result.claimed === true;
}

export function ackBestandsschutzEndNotice(
  stage: BestandsschutzEndStage,
  action: BestandsschutzEndAckAction,
): Promise<unknown> {
  return apiRequest('/api/membership/bestandsschutz-end/ack', {
    method: 'POST',
    body: { stage, action },
  });
}
